import { useEffect, useState } from "react";
import { Output, safeParse } from "valibot";

import { updateVariablesHelper } from "components/App/AppContext/helpers";
import { ChatUserFormData } from "shared/api";
import { getLocalStorageValue, getUserDataStorageKey } from "shared/functions";
import { useConfigContext, useGlobalsContext } from "shared/hooks";
import { VariablesEventSchema, VariablesRecordSchema } from "shared/schemas";

import { parseJson } from "./functions";
import useBotInfo from "./useBotInfo";

function useVariablesMessageListener() {
  const [isPopulatedFromMessageEvent, setIsPopulatedFromMessageEvent] =
    useState(false);

  const { setVariables } = useGlobalsContext();
  const { instanceName, token } = useConfigContext();
  const {
    botInfo: { id: botId },
  } = useBotInfo(token);

  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      const parsedData = parseJson(event.data);
      if (!parsedData.success) {
        return;
      }

      const result = safeParse(VariablesEventSchema, parsedData.parsedJson);

      if (!result.success) {
        return;
      }

      if (result.output.type === "set-botsonic-variables") {
        updateVariablesHelper(result.output.detail, setVariables);
      }

      setIsPopulatedFromMessageEvent(true);
    };

    window.addEventListener("message", messageHandler);

    return () => {
      window.removeEventListener("message", messageHandler);
    };
  }, [setVariables]);

  // * populate from storage if not populated from message
  useEffect(() => {
    if (isPopulatedFromMessageEvent) {
      return;
    }

    const storageKey = getLocalStorageValue(
      getUserDataStorageKey(instanceName, botId),
    );

    const parsedData = parseJson(storageKey);

    if (!parsedData.success) {
      return;
    }

    const data = parsedData.parsedJson;

    if (!data || !data.data_values) {
      return;
    }

    const variables = (data as ChatUserFormData).data_values.reduce(
      (acc: Output<typeof VariablesRecordSchema>, { key, value }) => {
        acc[key] = {
          key,
          value: `${value}`,
          fallback: "",
        };
        return acc;
      },
      {},
    );

    updateVariablesHelper(variables, setVariables);
  }, [botId, instanceName, isPopulatedFromMessageEvent, setVariables]);
}

export default useVariablesMessageListener;
